import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Table, TableProps, TableAddon } from 'tea-component';
import AddBtn, { IAddBtnProps } from './AddBtn';

import type { IBaseProps } from '@home/types/common';
import type { AutoTipOptions } from 'tea-component/lib/table/addons';

import './index.scss';

interface IAddableTableProps extends IBaseProps, TableProps {
  style?: React.CSSProperties;
  className?: string;
  tableStyle?: React.CSSProperties;
  tableClassName?: string;
  isInnerLoading?: boolean;
  autotipCfg?: AutoTipOptions;
  disabledAddBtn?: boolean;
  addBtnTips?: IAddBtnProps['tips'];
  addBtnText?: IAddBtnProps['text'];
  onAdd?: IAddBtnProps['onClick'];
}

const { autotip } = Table.addons;

const AddableTable: React.FC<IAddableTableProps> = (props) => {
  const {
    style,
    className,
    tableStyle,
    tableClassName,
    isInnerLoading = false,
    autotipCfg,
    disabledAddBtn,
    addBtnTips,
    addBtnText,
    addons: addonsProps,
    records,
    onAdd,
    ...restProps
  } = props;

  const isNoData = !records?.length;

  const addons = useMemo<TableAddon[] | undefined>(() => {
    const autotipAddon = autotip({
      ...autotipCfg,
      isLoading: isInnerLoading,
      emptyText: (
        <div style={{ height: 120.5 }}>
          <AddBtn
            className="dbg-abs-center"
            disabled={disabledAddBtn}
            tips={addBtnTips}
            text={addBtnText}
            onClick={onAdd}
          />
        </div>
      ),
    });
    if (!Array.isArray(addonsProps) || !addonsProps.length) return [autotipAddon];
    const addonsPropsTmp = addonsProps.filter((addon) => {
      const addonInfo = (addon as any)?.getInfo({ name: 'autotip' });
      return addonInfo?.name !== 'autotip';
    });
    return [...addonsPropsTmp, autotipAddon];
  }, [isInnerLoading, autotipCfg, addonsProps, disabledAddBtn, addBtnTips, addBtnText, onAdd]);

  return (
    <div
      style={style}
      className={classnames('dbg-addable-table-wrapper', className, {
        'no-data': isNoData,
        'inner-loading': isInnerLoading,
      })}
    >
      <Table
        {...restProps}
        style={tableStyle}
        className={classnames('dbg-addable-table', tableClassName)}
        addons={addons}
        records={isInnerLoading ? [] : records}
      />
      {!isInnerLoading && !isNoData && (
        <AddBtn disabled={disabledAddBtn} tips={addBtnTips} text={addBtnText} onClick={onAdd} />
      )}
    </div>
  );
};

export default React.memo(AddableTable);
